import { useEffect, useRef, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { Card, CardText, Button } from "react-bootstrap";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const RAW_DATA = [
  { name: "Action & Adventure", value: 59.5, revenue: "$25B" },
  { name: "Thriller & Horror",  value: 23.4, revenue: "$9.8B" },
  { name: "Comedy",             value: 8.3,  revenue: "$3.5B" },
  { name: "Drama",              value: 7.0,  revenue: "$2.9B" },
  { name: "Other",              value: 1.8,  revenue: "$756.1M" },
];

const COLORS = ["#cf2b2b", "#4b34e1", "#ff6a00", "#23c025", "#888888"];

export default function StoryDonut() {
  const containerRef = useRef();
  const animatedRef = useRef(RAW_DATA.map(d => ({ ...d, value: 0 })));
  const [animatedData, setAnimatedData] = useState(RAW_DATA.map(d => ({ ...d, value: 0 })));
 const [activeIndex, setActiveIndex] = useState(0); // was: useState(null)

  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top 80%",
        toggleActions: "play none none none",
      }
    });

    RAW_DATA.forEach((slice, i) => {
      tl.to(animatedRef.current[i], {
        value: slice.value,
        duration: 0.2,
        ease: "power2.out",
        onUpdate: () => setAnimatedData(animatedRef.current.map(d => ({ ...d })))
      }, 0);
    });

    return () => ScrollTrigger.killAll();
  }, []);

  const active = activeIndex !== null ? RAW_DATA[activeIndex] : null;

  return (
<div style={{ width: "100%", height: 320, display: "flex", gap: "1.5rem", }}>
  
  {/* LEFT: Text points */}
  <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "1rem" }}>
    <h11 
    style={{ 
      color: "white", 
      margin: 0, fontSize: "1rem", 
      fontWeight: 600, 
      letterSpacing: "0.05em" }}>
      Genre Market Share
    </h11>
   <ul style={{ color: "white", margin: 0, paddingLeft: '1.2rem', lineHeight: '1.8' }}>
  <li>Action films were the <strong>single top-grossing genre in 2025</strong>, with 69 releases generating <strong>$2.72B</strong> at the US box office.</li>
  <li>That equals <strong>31.2% of total domestic gross</strong> and roughly <strong>241 million tickets sold</strong>.</li>
  <li>Action and adventure films drive substantial <strong>merchandising revenue</strong> through action figures, clothing, and video games.</li>
  <li>Innovations in <strong>3D and IMAX</strong> make the genre prime for premium ticket pricing and strong global distribution.</li>
</ul>
    {/* <CardText style={{ color: "white", margin: 0 }}>
      The Spy Kids Effect: How the film's blend of action, comedy, and heart is designed to captivate audiences of all ages, much like the beloved Spy Kids franchise.
    </CardText> */}
  </div>

  {/* RIGHT: Donut chart */}
  <div
    ref={containerRef}
    style={{ flex: 1, height: "100%", display: "flex", flexDirection: "column" }}
  >
    <div style={{ flex: 1, position: "relative", minHeight: 0, filter: 'drop-shadow(0 0 5px rgba(192, 192, 192, 0.55))'  }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={animatedData}
            dataKey="value"
            nameKey="name"
            innerRadius={70}
            outerRadius={110}
            paddingAngle={3}
            stroke="rgba(210, 210, 220, 0.6)"   // 👈 silver border
            strokeWidth={1.5} 
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(0)}
          >
            {animatedData.map((entry, index) => (
              <Cell
                key={index}
                fill={COLORS[index]}
                opacity={activeIndex === null || activeIndex === index ? 1 : 0.35}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* Center Text */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          color: "white",
          textAlign: "center",
          pointerEvents: "none",
          width: 130,
        }}
      >
        {active ? (
          <>
            <div style={{ fontSize: "0.72rem", opacity: 0.6, marginBottom: 2, lineHeight: 1.2 }}>
              {active.name}
            </div>
            <div style={{ fontSize: "1.8rem", fontWeight: "bold", lineHeight: 1 }}>
              {active.value}%
            </div>
            <div style={{ fontSize: "0.85rem", opacity: 0.75, marginTop: 4 }}>
              {active.revenue}
            </div>
          </>
        ) : null}
      </div>
    </div>

    {/* Legend */}
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: "0.6rem 0.9rem",
        paddingTop: "0.5rem",
      }}
    >
      {RAW_DATA.map((entry, index) => (
        <div
          key={entry.name}
          onMouseEnter={() => setActiveIndex(index)}
          onMouseLeave={() => setActiveIndex(0)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.4rem",
            cursor: "pointer",
            opacity: activeIndex === index ? 1 : 0.6,
            transition: "opacity 0.2s ease",
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: COLORS[index],
              flexShrink: 0,
            }}
          />
          <span 
          style={{ 
                color: 'silver', 
                fontFamily: "'Buzzer', sans-serif",
                fontSize: 'clamp(1.5rem, 5vw, 1.5rem)'
              }}
          >
            {entry.name}
          </span>
        </div>
      ))}
    </div>
  </div>

</div>
  );
}